import { trackPrefetchSummary } from "@/main/analytics/analyticsEventsCore";
import { destinationKeyForUrl, resolveDestinationComponent } from "@/main/navigation/NavigationPrefetchTargets";

// Efficiency telemetry for navigation-intent prefetch: how many prefetches we ISSUE vs how many the
// user actually CONSUMES (navigates to), split into the two cost classes — cheap per-component `chunk`
// vs expensive per-URL `data`. wasted = issued − consumed. Surfaces as a per-session GA4 `prefetch_summary`
// event and a live `window.__seavienPrefetchStats()` for dev tuning / Playwright.
// See designs/01-now/2026-07-09-navigation-intent-route-prefetch-design.md §2d.

type PrefetchKind = "chunk" | "data";

interface KindCounters {
    issued: Set<string>;
    consumed: Set<string>;
}

const counters: Record<PrefetchKind, KindCounters> = {
    chunk: { issued: new Set(), consumed: new Set() },
    data: { issued: new Set(), consumed: new Set() },
};

// Snapshot of counts at the last field flush, so each flush emits only the delta since the last one
// (a page can go hidden many times — deltas keep the GA4 aggregate from double-counting).
const lastFlushed = { chunkIssued: 0, chunkConsumed: 0, dataIssued: 0, dataConsumed: 0 };

let telemetryInstalled = false;

export function recordPrefetchIssued(kind: PrefetchKind, key: string): void {
    counters[kind].issued.add(key);
}

function recordPrefetchConsumed(kind: PrefetchKind, key: string): void {
    // Only counts as consumed if we actually prefetched it (an un-prefetched navigation is not a hit).
    if (counters[kind].issued.has(key)) {
        counters[kind].consumed.add(key);
    }
}

/** Called at soft-navigation commit: mark any prior prefetch of this destination as consumed. */
export function recordNavigationConsumedPrefetch(url: URL): void {
    recordPrefetchConsumed("data", destinationKeyForUrl(url));
    const component = resolveDestinationComponent(url);
    if (component) {
        recordPrefetchConsumed("chunk", component);
    }
}

export interface PrefetchStatsSnapshot {
    chunk: { issued: number; consumed: number; wasted: number };
    data: { issued: number; consumed: number; wasted: number };
}

export function getPrefetchStats(): PrefetchStatsSnapshot {
    const snapshot = (kind: PrefetchKind) => {
        const issued = counters[kind].issued.size;
        const consumed = counters[kind].consumed.size;
        return { issued, consumed, wasted: Math.max(0, issued - consumed) };
    };
    return { chunk: snapshot("chunk"), data: snapshot("data") };
}

function flushPrefetchSummary(): void {
    const stats = getPrefetchStats();
    const delta = {
        chunkIssued: stats.chunk.issued - lastFlushed.chunkIssued,
        chunkConsumed: stats.chunk.consumed - lastFlushed.chunkConsumed,
        dataIssued: stats.data.issued - lastFlushed.dataIssued,
        dataConsumed: stats.data.consumed - lastFlushed.dataConsumed,
    };
    if (delta.chunkIssued <= 0 && delta.dataIssued <= 0) {
        return; // nothing new to report since the last flush
    }
    lastFlushed.chunkIssued = stats.chunk.issued;
    lastFlushed.chunkConsumed = stats.chunk.consumed;
    lastFlushed.dataIssued = stats.data.issued;
    lastFlushed.dataConsumed = stats.data.consumed;
    try {
        trackPrefetchSummary(delta);
    } catch {
        // Telemetry is strictly side-effect-only.
    }
}

function handleVisibilityFlush(): void {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        flushPrefetchSummary();
    }
}

export function installPrefetchTelemetry(): void {
    if (telemetryInstalled || typeof window === "undefined") {
        return;
    }
    telemetryInstalled = true;
    document.addEventListener("visibilitychange", handleVisibilityFlush);
    window.addEventListener("pagehide", flushPrefetchSummary);
    (window as Window & { __seavienPrefetchStats?: () => PrefetchStatsSnapshot }).__seavienPrefetchStats =
        getPrefetchStats;
}

export function resetPrefetchTelemetryForTests(): void {
    if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityFlush);
    }
    if (typeof window !== "undefined") {
        window.removeEventListener("pagehide", flushPrefetchSummary);
        delete (window as Window & { __seavienPrefetchStats?: unknown }).__seavienPrefetchStats;
    }
    counters.chunk.issued.clear();
    counters.chunk.consumed.clear();
    counters.data.issued.clear();
    counters.data.consumed.clear();
    lastFlushed.chunkIssued = 0;
    lastFlushed.chunkConsumed = 0;
    lastFlushed.dataIssued = 0;
    lastFlushed.dataConsumed = 0;
    telemetryInstalled = false;
}
